import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Tooltip,
  Typography,
} from "@mui/material";

import PageBanner from "../../components/PageBanner";
import { fetchMyPatientQuestionnaireVisits } from "../../services/patientQuestionnaireService";
import { formatDate } from "../../utils/dateFormat";

/** Navbar / portal accent — matches app theme */
const THEME_ACCENT = "#3f7fbf";
const THEME_CARD_BG = "#dce8ef";

function parseLocalDate(iso) {
  if (!iso) return null;
  const s = String(iso).slice(0, 10);
  const [y, m, d] = s.split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

function isPastWindowClose(row) {
  const close = parseLocalDate(row.window_close_date);
  if (!close) return false;
  const today = new Date();
  today.setHours(12, 0, 0, 0);
  close.setHours(12, 0, 0, 0);
  return today > close;
}

function cardSxForRow(row) {
  if (row.questionnaires_completed || row.visit_status === "completed") {
    return {
      borderLeft: 6,
      borderColor: "#2e7d32",
      bgcolor: "#e8f5e9",
      borderTop: "1px solid rgba(46, 125, 50, 0.28)",
      borderRight: "1px solid rgba(46, 125, 50, 0.28)",
      borderBottom: "1px solid rgba(46, 125, 50, 0.28)",
      boxShadow: "0 2px 8px rgba(46, 125, 50, 0.12)",
    };
  }
  const v = row.visit_status;
  if (v === "due") {
    return {
      borderLeft: 6,
      borderColor: "#d32f2f",
      bgcolor: "#ffebee",
      borderTop: "1px solid rgba(211, 47, 47, 0.25)",
      borderRight: "1px solid rgba(211, 47, 47, 0.25)",
      borderBottom: "1px solid rgba(211, 47, 47, 0.25)",
      boxShadow: "0 2px 8px rgba(211, 47, 47, 0.12)",
    };
  }
  if (v === "upcoming") {
    return {
      borderLeft: 6,
      borderColor: THEME_ACCENT,
      bgcolor: THEME_CARD_BG,
      borderTop: `1px solid rgba(63, 127, 191, 0.2)`,
      borderRight: `1px solid rgba(63, 127, 191, 0.2)`,
      borderBottom: `1px solid rgba(63, 127, 191, 0.2)`,
      boxShadow: "0 2px 8px rgba(26, 36, 90, 0.08)",
    };
  }
  if (v === "in_window") {
    return {
      borderLeft: 6,
      borderColor: "#1a245a",
      bgcolor: "#c7d7df",
      borderTop: `1px solid rgba(26, 36, 90, 0.15)`,
      borderRight: `1px solid rgba(26, 36, 90, 0.15)`,
      borderBottom: `1px solid rgba(26, 36, 90, 0.15)`,
      boxShadow: "0 2px 8px rgba(26, 36, 90, 0.1)",
    };
  }
  return {
    borderLeft: 6,
    borderColor: "#90a4ae",
    bgcolor: "#f5f7f9",
    borderTop: "1px solid #e0e0e0",
    borderRight: "1px solid #e0e0e0",
    borderBottom: "1px solid #e0e0e0",
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
  };
}

function statusTitleColor(row) {
  if (row.questionnaires_completed || row.visit_status === "completed") return "#1b5e20";
  const v = row.visit_status;
  if (v === "due") return "#c62828";
  if (v === "upcoming") return "#1a245a";
  if (v === "in_window") return "#1565c0";
  return "#455a64";
}

const PatientQuestionnaireList = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [rows, setRows] = useState([]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const data = await fetchMyPatientQuestionnaireVisits();
        if (!cancelled) {
          setRows(Array.isArray(data) ? data : []);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err?.message || "Failed to load questionnaires.");
          setRows([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const statusLabel = (row) => {
    if (row.questionnaires_completed || row.visit_status === "completed") return "Completed";
    const v = row.visit_status;
    if (v === "in_window") return "In window";
    if (v === "upcoming") return "Upcoming";
    if (v === "misconfigured") return "No timeline match";
    if (v === "due") {
      return isPastWindowClose(row) ? "Past due" : "Due";
    }
    return v || "—";
  };

  const canFill = (row) =>
    !row.questionnaires_completed &&
    row.visit_status !== "completed" &&
    row.fill_allowed !== false &&
    row.questionnaires_configured !== false;

  const canViewAnswers = (row) => Boolean(row.questionnaires_completed) || row.visit_status === "completed";

  return (
    <Box sx={{ bgcolor: "#c7d7df", minHeight: "100vh" }}>
      <PageBanner title="My questionnaires" />

      <Container maxWidth={false} disableGutters sx={{ py: 3, px: { xs: 2, sm: 2.5, md: 3 }, width: "100%", maxWidth: "100%" }}>
        {loading ? (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 6 }}>
            <CircularProgress sx={{ color: THEME_ACCENT }} />
            <Typography sx={{ mt: 2 }}>Loading…</Typography>
          </Box>
        ) : error ? (
          <Typography color="error">{error}</Typography>
        ) : rows.length === 0 ? (
          <Card sx={{ p: 2, borderRadius: 2 }}>
            <Typography>No questionnaire visits to show.</Typography>
          </Card>
        ) : (
          <Box
            sx={{
              display: "grid",
              width: "100%",
              gap: 2.5,
              alignItems: "stretch",
              gridTemplateColumns: {
                xs: "minmax(0, 1fr)",
                sm: "repeat(2, minmax(0, 1fr))",
                md: "repeat(3, minmax(0, 1fr))",
                lg: "repeat(4, minmax(0, 1fr))",
                xl: "repeat(5, minmax(0, 1fr))",
              },
            }}
          >
            {rows.map((row) => (
              <Card
                key={`${row.execution_id}-${row.schedule_id}`}
                elevation={0}
                sx={{
                  minWidth: 0,
                  borderRadius: 2,
                  overflow: "visible",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  ...cardSxForRow(row),
                }}
              >
                <CardContent
                  sx={{
                    py: 3,
                    px: 3,
                    "&:last-child": { pb: 3 },
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="overline"
                        sx={{
                          fontWeight: 700,
                          letterSpacing: 0.8,
                          color: statusTitleColor(row),
                        }}
                      >
                        {statusLabel(row)}
                      </Typography>
                      <Typography variant="h6" fontWeight={700} sx={{ color: "#1f2f3f", mt: 0.25, fontSize: "1.1rem" }}>
                        {row.project_name || "Study visit"}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Visit schedule {row.schedule_order}
                      </Typography>
                      <Box sx={{ mt: 1.5, display: "flex", flexDirection: "column", gap: 0.5 }}>
                        <Typography variant="body2" sx={{ fontSize: "0.875rem" }}>
                          <strong>Optimal:</strong> {row.optimal_date == null ? "—" : formatDate(row.optimal_date)}
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: "0.875rem" }}>
                          <strong>Window:</strong>{" "}
                          {row.window_open_date && row.window_close_date
                            ? `${formatDate(row.window_open_date)} – ${formatDate(row.window_close_date)}`
                            : "—"}
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ mt: 2, pt: 2, borderTop: "1px solid rgba(0,0,0,0.06)" }}>
                      {canViewAnswers(row) ? (
                        <Button
                          variant="contained"
                          color="success"
                          size="medium"
                          fullWidth
                          onClick={() =>
                            navigate(`/patient/questionnaires/${row.execution_id}/${row.schedule_id}`)
                          }
                          sx={{ whiteSpace: "nowrap" }}
                        >
                          View answers
                        </Button>
                      ) : canFill(row) ? (
                        <Button
                          variant="contained"
                          color="primary"
                          size="medium"
                          fullWidth
                          onClick={() =>
                            navigate(`/patient/questionnaires/${row.execution_id}/${row.schedule_id}`)
                          }
                          sx={{ whiteSpace: "nowrap" }}
                        >
                          Fill questionnaire
                        </Button>
                      ) : row.questionnaires_configured === false ? (
                        <Tooltip
                          title={
                            row.visit_status === "misconfigured"
                              ? "This visit does not match a project timeline row. Ask your site to check visit order vs project setup."
                              : "No questionnaires are linked to this visit on the project timeline yet."
                          }
                        >
                          <span style={{ display: "block" }}>
                            <Button variant="outlined" size="medium" disabled fullWidth>
                              {row.visit_status === "misconfigured" ? "Setup issue" : "None assigned"}
                            </Button>
                          </span>
                        </Tooltip>
                      ) : (
                        <Tooltip title="Opens when your visit window starts.">
                          <span style={{ display: "block" }}>
                            <Button variant="outlined" size="medium" disabled fullWidth>
                              Not available yet
                            </Button>
                          </span>
                        </Tooltip>
                      )}
                    </Box>
                  </CardContent>
              </Card>
            ))}
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default PatientQuestionnaireList;
